import Autocomplete from '@components/v2/form/Autocomplete/Autocomplete';
import Icon from '@components/v2/ui/Icon/Icon';
import Typography from '@components/v2/ui/Typography/Typography';
import { MenuItem, Stack } from '@mui/material';
import {
  AutocompleteChangeReason,
  FilterOptionsState,
  createFilterOptions,
} from '@mui/material/useAutocomplete';
import { Plus } from 'lucide-react';
import { ControllerRenderProps } from 'react-hook-form';
import { useListPick } from '../../../components/ListPicker/useListPick';
import SellerOption from '../../../components/MyNetwork/SellerOption';
import LangEnum from '../../../lang/LangEnum';
import snackbarStore from '../../../stores/Snackbar.store';
import { onScrollEndReload } from '../../../utils/generics';
import { isValidEmail } from '../../../utils/validators';
import { ShareContact } from '../../customer/types/customerContact.types';
import { useTranslation } from '../../translation/TranslationContext';
import { ISharedEntityForm } from '../types/sharedEntities.types';

interface IEmailAutocomplete {
  entityId?: string;
  field: ControllerRenderProps<ISharedEntityForm, 'config.emails'>;
}

const getContactLabel = (option: ShareContact) => {
  if (option.id === 'new') {
    return option.newEmail || '-';
  }

  return option.email || '-';
};

const EmailAutocomplete = ({ field, entityId }: IEmailAutocomplete) => {
  const { translate } = useTranslation();
  const listPickActions = useListPick<ShareContact>({
    url: `/catalogs/${entityId}/share-contacts`,
    order: { name: 'ASC' },
    queryField: 'query',
    disabled: !entityId,
  });
  const { handleMessage } = snackbarStore;

  const shareContacts = listPickActions.leftItems;

  const handleUpdateContactList = (items: ShareContact[], reason: AutocompleteChangeReason) => {
    if (reason === 'removeOption') {
      field.onChange(items);
      return;
    }

    if (reason !== 'selectOption') {
      return;
    }

    if (items.length === 0) {
      field.onChange(items);
      return;
    }

    const notValidEmail = items.find((option) =>
      option.id === 'new' ? !isValidEmail(option.newEmail) : !isValidEmail(option.email),
    );

    if (notValidEmail) {
      handleMessage({ type: 'error', message: translate(LangEnum.GLOBAL_INVALID_EMAIL) });
      return;
    }

    field.onChange(items);
  };

  const filterWithCreateOption = (
    options: ShareContact[],
    params: FilterOptionsState<ShareContact>,
  ) => {
    const filtered = createFilterOptions<ShareContact>()(options, params);

    if (
      params.inputValue?.length &&
      !filtered.find((option) => option.email.includes(params.inputValue))
    ) {
      filtered.push({
        email: `${translate(LangEnum.GLOBAL_ADD)} "${params.inputValue}"`,
        name: params.inputValue,
        newEmail: params.inputValue,
        id: 'new',
      });
    }

    return filtered;
  };

  return (
    <Autocomplete<ShareContact, true>
      multiple
      options={shareContacts}
      value={field.value}
      loading={listPickActions.loading}
      getOptionLabel={getContactLabel}
      filterOptions={filterWithCreateOption}
      isOptionEqualToValue={(option, value) => option.id === value.id}
      noOptionsText={translate(LangEnum.GLOBAL_NO_OPTIONS)}
      loadingText={translate(LangEnum.GLOBAL_SEARCHING)}
      textFieldProps={{ placeholder: LangEnum.GLOBAL_EMAIL_PLACEHOLDER }}
      slotProps={{
        listbox: {
          style: { maxHeight: 248 },
          onScroll: (event: React.SyntheticEvent) => onScrollEndReload(event, listPickActions),
        },
      }}
      renderOption={(props, option, state) => {
        const { key, ...optionProps } = props;

        if (option.id === 'new') {
          return (
            <MenuItem {...optionProps} key={key}>
              <Stack
                direction="row"
                spacing={1}
                sx={{ alignItems: 'center', color: 'primary.main' }}
              >
                <Icon icon={Plus} size="sm" />
                <Typography color="inherit">{option.email}</Typography>
              </Stack>
            </MenuItem>
          );
        }

        return (
          <MenuItem {...optionProps} key={key}>
            <SellerOption selected={state.selected} name={option.name} email={option.email} />
          </MenuItem>
        );
      }}
      onInputChange={(_, query, reason) => {
        if (reason !== 'input') {
          return;
        }

        listPickActions.onSearch(query);
      }}
      onChange={(_, items, reason) => handleUpdateContactList(items as ShareContact[], reason)}
    />
  );
};

export default EmailAutocomplete;
